/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Environment, SubmissionStatus, IncidentStatus, IncidentPriority } from "@/lib/types";

// ============================================================================
// Types
// ============================================================================

interface DateRange {
  from: string | null;
  to: string | null;
}

interface FiltersState {
  // Global filters (affect all pages)
  environment: Environment | "all";
  stateCode: string | null;
  dateRange: DateRange;

  // Submission filters
  submissionSearch: string;
  submissionStatuses: SubmissionStatus[];
  submissionPage: number;
  submissionPageSize: number;
  submissionSortBy: string;
  submissionSortOrder: "asc" | "desc";

  // Incident filters
  incidentSearch: string;
  incidentStatuses: IncidentStatus[];
  incidentPriorities: IncidentPriority[];
  incidentAssignee: string | null;
  incidentPage: number;
  incidentPageSize: number;

  // Actions
  setEnvironment: (environment: Environment | "all") => void;
  setStateCode: (stateCode: string | null) => void;
  setDateRange: (range: DateRange) => void;

  // Submission filter actions
  setSubmissionSearch: (search: string) => void;
  setSubmissionStatuses: (statuses: SubmissionStatus[]) => void;
  setSubmissionPage: (page: number) => void;
  setSubmissionPageSize: (pageSize: number) => void;
  setSubmissionSort: (sortBy: string, sortOrder: "asc" | "desc") => void;
  resetSubmissionFilters: () => void;

  // Incident filter actions
  setIncidentSearch: (search: string) => void;
  setIncidentStatuses: (statuses: IncidentStatus[]) => void;
  setIncidentPriorities: (priorities: IncidentPriority[]) => void;
  setIncidentAssignee: (assignee: string | null) => void;
  setIncidentPage: (page: number) => void;
  setIncidentPageSize: (pageSize: number) => void;
  resetIncidentFilters: () => void;

  // Reset all
  resetAllFilters: () => void;
}

// ============================================================================
// Default values
// ============================================================================

const DEFAULT_PAGE_SIZE = 25;

const defaultSubmissionFilters = {
  submissionSearch: "",
  submissionStatuses: [] as SubmissionStatus[],
  submissionPage: 1,
  submissionPageSize: DEFAULT_PAGE_SIZE,
  submissionSortBy: "submittedAt",
  submissionSortOrder: "desc" as const,
};

const defaultIncidentFilters = {
  incidentSearch: "",
  incidentStatuses: [] as IncidentStatus[],
  incidentPriorities: [] as IncidentPriority[],
  incidentAssignee: null,
  incidentPage: 1,
  incidentPageSize: DEFAULT_PAGE_SIZE,
};

// ============================================================================
// Store
// ============================================================================

export const useFiltersStore = create<FiltersState>()(
  persist(
    (set) => ({
      // Initial global filters
      environment: "all",
      stateCode: null,
      dateRange: { from: null, to: null },

      // Initial submission filters
      ...defaultSubmissionFilters,

      // Initial incident filters
      ...defaultIncidentFilters,

      // Global filter actions
      setEnvironment: (environment) => set({ environment }),

      setStateCode: (stateCode) => set({ stateCode }),

      setDateRange: (range) => set({ dateRange: range }),

      // Submission filter actions
      setSubmissionSearch: (search) => set({ submissionSearch: search, submissionPage: 1 }),

      setSubmissionStatuses: (statuses) => set({ submissionStatuses: statuses, submissionPage: 1 }),

      setSubmissionPage: (page) => set({ submissionPage: page }),

      setSubmissionPageSize: (pageSize) => set({ submissionPageSize: pageSize, submissionPage: 1 }),

      setSubmissionSort: (sortBy, sortOrder) =>
        set({ submissionSortBy: sortBy, submissionSortOrder: sortOrder }),

      resetSubmissionFilters: () => set(defaultSubmissionFilters),

      // Incident filter actions
      setIncidentSearch: (search) => set({ incidentSearch: search, incidentPage: 1 }),

      setIncidentStatuses: (statuses) => set({ incidentStatuses: statuses, incidentPage: 1 }),

      setIncidentPriorities: (priorities) =>
        set({ incidentPriorities: priorities, incidentPage: 1 }),

      setIncidentAssignee: (assignee) => set({ incidentAssignee: assignee, incidentPage: 1 }),

      setIncidentPage: (page) => set({ incidentPage: page }),

      setIncidentPageSize: (pageSize) => set({ incidentPageSize: pageSize, incidentPage: 1 }),

      resetIncidentFilters: () => set(defaultIncidentFilters),

      // Reset all filters
      resetAllFilters: () =>
        set({
          environment: "all",
          stateCode: null,
          dateRange: { from: null, to: null },
          ...defaultSubmissionFilters,
          ...defaultIncidentFilters,
        }),
    }),
    {
      name: "torqe-filters",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        // Persist global filters and sort preferences
        environment: state.environment,
        stateCode: state.stateCode,
        submissionSortBy: state.submissionSortBy,
        submissionSortOrder: state.submissionSortOrder,
        submissionPageSize: state.submissionPageSize,
        incidentPageSize: state.incidentPageSize,
      }),
    }
  )
);

// ============================================================================
// Fine-grained Selectors
// ============================================================================

// Global filters
export const useEnvironment = () => useFiltersStore((s) => s.environment);
export const useStateCode = () => useFiltersStore((s) => s.stateCode);
export const useDateRange = () => useFiltersStore((s) => s.dateRange);

// Submission filters as object (for API calls)
export const useSubmissionFilters = () =>
  useFiltersStore((s) => ({
    search: s.submissionSearch,
    environment: s.environment !== "all" ? s.environment : undefined,
    stateCode: s.stateCode || undefined,
    status: s.submissionStatuses.length > 0 ? s.submissionStatuses : undefined,
    dateFrom: s.dateRange.from || undefined,
    dateTo: s.dateRange.to || undefined,
    page: s.submissionPage,
    pageSize: s.submissionPageSize,
    sortBy: s.submissionSortBy,
    sortOrder: s.submissionSortOrder,
  }));

// Incident filters as object (for API calls)
export const useIncidentFilters = () =>
  useFiltersStore((s) => ({
    search: s.incidentSearch,
    stateCode: s.stateCode || undefined,
    status: s.incidentStatuses.length > 0 ? s.incidentStatuses : undefined,
    priority: s.incidentPriorities.length > 0 ? s.incidentPriorities : undefined,
    assignedTo: s.incidentAssignee || undefined,
    dateFrom: s.dateRange.from || undefined,
    dateTo: s.dateRange.to || undefined,
    page: s.incidentPage,
    pageSize: s.incidentPageSize,
  }));

// Individual submission filter selectors
export const useSubmissionSearch = () => useFiltersStore((s) => s.submissionSearch);
export const useSubmissionStatuses = () => useFiltersStore((s) => s.submissionStatuses);
export const useSubmissionPage = () => useFiltersStore((s) => s.submissionPage);
export const useSubmissionPageSize = () => useFiltersStore((s) => s.submissionPageSize);
export const useSubmissionSort = () =>
  useFiltersStore((s) => ({
    sortBy: s.submissionSortBy,
    sortOrder: s.submissionSortOrder,
  }));

// Individual incident filter selectors
export const useIncidentSearch = () => useFiltersStore((s) => s.incidentSearch);
export const useIncidentStatuses = () => useFiltersStore((s) => s.incidentStatuses);
export const useIncidentPriorities = () => useFiltersStore((s) => s.incidentPriorities);
export const useIncidentAssignee = () => useFiltersStore((s) => s.incidentAssignee);
export const useIncidentPage = () => useFiltersStore((s) => s.incidentPage);
export const useIncidentPageSize = () => useFiltersStore((s) => s.incidentPageSize);

// ============================================================================
// Action Selectors (Setters)
// ============================================================================

export const useSetEnvironment = () => useFiltersStore((s) => s.setEnvironment);
export const useSetStateCode = () => useFiltersStore((s) => s.setStateCode);
export const useSetDateRange = () => useFiltersStore((s) => s.setDateRange);

// Submission setters
export const useSetSubmissionSearch = () => useFiltersStore((s) => s.setSubmissionSearch);
export const useSetSubmissionStatuses = () => useFiltersStore((s) => s.setSubmissionStatuses);
export const useSetSubmissionPage = () => useFiltersStore((s) => s.setSubmissionPage);
export const useSetSubmissionPageSize = () => useFiltersStore((s) => s.setSubmissionPageSize);
export const useSetSubmissionSort = () => useFiltersStore((s) => s.setSubmissionSort);
export const useResetSubmissionFilters = () => useFiltersStore((s) => s.resetSubmissionFilters);

// Incident setters
export const useSetIncidentSearch = () => useFiltersStore((s) => s.setIncidentSearch);
export const useSetIncidentStatuses = () => useFiltersStore((s) => s.setIncidentStatuses);
export const useSetIncidentPriorities = () => useFiltersStore((s) => s.setIncidentPriorities);
export const useSetIncidentAssignee = () => useFiltersStore((s) => s.setIncidentAssignee);
export const useSetIncidentPage = () => useFiltersStore((s) => s.setIncidentPage);
export const useSetIncidentPageSize = () => useFiltersStore((s) => s.setIncidentPageSize);
export const useResetIncidentFilters = () => useFiltersStore((s) => s.resetIncidentFilters);

// Reset all
export const useResetAllFilters = () => useFiltersStore((s) => s.resetAllFilters);

// ============================================================================
// Alias Exports for convenience
// ============================================================================

// Generic search query (for backward compatibility)
export const useSearchQuery = () => useFiltersStore((s) => s.submissionSearch);
export const useSetSearchQuery = () => useFiltersStore((s) => s.setSubmissionSearch);
